/**
 * The documentation that ships with a version — the installation and
 * management guides, the change-log document — as the command center's
 * `ReleaseDocuments` lists them, minus its "Add document" button: a reseller
 * reads what XOP wrote.
 *
 * These are ordinary documentation articles pinned to the version, already
 * narrowed upstream by `Documents::AccessResolver` to what THIS reseller may
 * read — the same answer Documentation gives. A version's list carries
 * its release's guides folded in (a version inherits them). Each row has a
 * **View** button (Documentation's article reader; its Back returns here) and
 * a **Download** button (2026-09-17, CEO) that saves exactly what the reader's
 * Download saves — `downloadDocument`, one behaviour for both.
 *
 * TWO pages mount this list (2026-09-17): the Releases version pane, and
 * Documentation ▸ Release documents (`/documentation/releases/:release`),
 * which the CEO asked for so a version's documents are also found from the
 * documentation. It is the same component over the same request, so the two
 * can never disagree. Documentation's library shelf (folders, Featured,
 * Recently added) still does not list them. Trainings are never here —
 * portal-api filters `purpose: "training"` out of this endpoint.
 */
import { Alert, Box, Button, Chip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Download, Eye } from "lucide-react";
import { type JSX, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { PageLoader } from "../../../components/ui/page-loader.js";
import { getDoc } from "../../knowledge-base/api.js";
import { downloadDocument } from "../../knowledge-base/downloadDocument.js";
import { kbArticlePath } from "../../knowledge-base/kb-paths.js";
import { DownloadRefused, type ReleaseDoc, type ReleaseRow, listReleaseDocuments } from "./api.js";

const DOC_TYPE_LABEL: Record<string, string> = {
  guide: "Guide",
  article: "Article",
  video: "Video",
  faq: "FAQ",
  slide: "Slides",
  file: "File",
};

/** What a document's audience reads as on the row. `feature` names the
 *  features so a reseller knows which of its customers the guide is for. */
export function audienceLabel(doc: ReleaseDoc): string {
  const access = doc.access;
  switch (access?.scope) {
    case "feature": {
      const names = (access.features ?? []).map((f) => f.name ?? f.slug);
      return names.length ? `For ${names.join(", ")} customers` : "For feature owners";
    }
    case "product": {
      const names = (access.products ?? []).map((p) => p.name ?? p.code);
      return names.length ? `For ${names.join(", ")} customers` : "For product owners";
    }
    case "release":
      return "Customers on this release";
    default:
      return "Everyone";
  }
}

export function ReleaseDocuments({
  release,
  back,
}: {
  release: ReleaseRow;
  /** Where the reader's Back should return, handed over in router state.
   *  Documentation ▸ Release documents passes its own release page; the
   *  Releases page passes nothing, and the reader's Back goes to Releases. */
  back?: { to: string; label: string };
}): JSX.Element {
  const query = useQuery({
    queryKey: ["reseller", "release-documents", release.id],
    queryFn: () => listReleaseDocuments(release.id),
  });
  const docs: ReleaseDoc[] = query.data ?? [];
  const [downloading, setDownloading] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // The row carries a summary only; the reader's own detail decides WHAT is
  // saved (original upload, or a PDF of an authored body).
  async function download(doc: ReleaseDoc): Promise<void> {
    setDownloading(doc.id);
    setDownloadError(null);
    try {
      await downloadDocument((await getDoc(doc.slug)).data);
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : `Could not download ${doc.title}`);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <Box sx={{ display: "grid", gap: 1 }} data-testid={`release-${release.version}-documents`}>
      {query.isLoading ? (
        <PageLoader />
      ) : query.isError ? (
        <Alert
          severity="error"
          sx={{ fontSize: 13 }}
          action={
            <Button size="small" onClick={() => void query.refetch()}>
              Retry
            </Button>
          }
        >
          {query.error instanceof DownloadRefused && query.error.message
            ? query.error.message
            : "Could not load this version's documents."}
        </Alert>
      ) : docs.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          No documents ship with {release.version} yet.
        </Typography>
      ) : (
        <>
        {downloadError ? (
          <Alert severity="error" sx={{ fontSize: 13 }} onClose={() => setDownloadError(null)}>
            {downloadError}
          </Alert>
        ) : null}
        {docs.map((doc) => (
          <Box
            key={doc.id}
            data-testid={`release-${release.version}-doc-${doc.id}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "inline-flex", color: "primary.main", flexShrink: 0 }}>
              <BookOpen size={18} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography
                  component={RouterLink}
                  to={kbArticlePath(doc.slug)}
                  {...(back ? { state: { back } } : {})}
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "text.primary",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {doc.title}
                </Typography>
                <Chip
                  label={DOC_TYPE_LABEL[doc.doc_type] ?? doc.doc_type}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18 }}
                />
              </Box>
              <Typography sx={{ fontSize: 11, color: "text.disabled" }}>
                {audienceLabel(doc)}
                {doc.category ? ` · ${doc.category.name}` : " · not filed in a folder"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Eye size={15} />}
                component={RouterLink}
                to={kbArticlePath(doc.slug)}
                {...(back ? { state: { back } } : {})}
                data-testid={`release-doc-${doc.id}-view`}
              >
                View
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<Download size={15} />}
                disabled={downloading === doc.id}
                onClick={() => void download(doc)}
                data-testid={`release-doc-${doc.id}-download`}
              >
                {downloading === doc.id ? "Preparing…" : "Download"}
              </Button>
            </Box>
          </Box>
        ))}
        </>
      )}
    </Box>
  );
}
